import { ApiRetailerPaymentType, ApiTax } from '@vend/types'
import {
  add,
  divide,
  isFinite,
  multiply,
  round,
  subtract,
  vn,
} from 'vend-number'
import {
  LineItem,
  LineItemTaxComponent,
  LineItemTotals,
} from '../models/line-item'
import { Payment } from '../models/payment'
import {
  EcomCustomCharges,
  Finals,
  OriginalSurcharge,
  Sale,
  SaleTax,
  Totals,
} from '../models/sale'
import { SaleStatus } from '../models/sale.constants'
import { SaleAdjustment } from '../../types/adjustments'
import { isReturn } from '../functions/sale'
import {
  BaseLineItemTotalsCalculationParams,
  LineItemTotalsAndTaxComponents,
  calculateLineItemTotals,
  isExcludedFromSaleDiscount,
} from './calculate-line-item'
import { isEligibleForCashDiscount } from './calculate-payment'
import { calculateTaxInclusivePriceComponents } from './calculate-price'

const SALE_PRICE_TAX_DECIMAL_PLACES = 5

export interface SaleDiscountTotals {
  maxSaleDiscount: string | number
  discountPercentAsDecimal: string | number
}

interface MinSaleDiscount {
  price: string | number
  tax: string | number
}

interface SurchargeArgs {
  amount: number
  rules: string[]
  origin: OriginalSurcharge | null
}

type DiscountTotalsCalculationParams = DiscountableTotalCalculationParams &
  Omit<DiscountCalculationParams, 'maxSaleDiscount'>

/**
 * Calculate the discount totals for the given sale. This includes the discountable total (maxSaleDiscount) and the
 * discount percentage (discountPercentAsDecimal).
 *
 * @param sale The sale to determine the discount totals of
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @returns the discountable total (maxSaleDiscount) and the discount percentage (discountPercentAsDecimal)
 */
export function calculateSaleDiscountTotals(
  params: DiscountTotalsCalculationParams
): SaleDiscountTotals {
  const maxSaleDiscount = calculateDiscountableTotal(params)
  const discountPercentAsDecimal = calculateDiscountPercentageAsDecimal({
    ...params,
    maxSaleDiscount,
  })
  return {
    maxSaleDiscount,
    discountPercentAsDecimal,
  }
}

interface DiscountableTotalCalculationParams {
  lineItems: Array<Readonly<LineItem>>
  saleIsConfirmed: boolean
  saleIsReturn: boolean
  saleIsSavedUnfulfilled?: boolean
  isTaxInclusive: boolean
}

/**
 * Calculate discountable total based on the provided lineItems and status for a sale.
 *
 * @param sale The sale to determine the discountable total of
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @returns the maximum amount the given sale can be discounted by
 */
export function calculateDiscountableTotal({
  lineItems,
  saleIsReturn,
  saleIsConfirmed,
  saleIsSavedUnfulfilled,
  isTaxInclusive,
}: DiscountableTotalCalculationParams): number {
  return lineItems.reduce(
    (
      total,
      { quantity, price, tax, isReturn, confirmed, isEditableForContinue }
    ) => {
      // In a return sale, only return items count towards the discountable
      // total. Otherwise, exclude any line item that isn't eligible for the
      // sale discount (see `isExcludedFromSaleDiscount`): items added after a
      // layby/on account sale was confirmed, and items on a saved unfulfilled
      // sale that aren't part of the original sale and aren't editable as part
      // of the current continuation.
      if (
        (saleIsReturn && !isReturn) ||
        isExcludedFromSaleDiscount({
          confirmed,
          isEditableForContinue,
          saleIsConfirmed,
          saleIsSavedUnfulfilled,
        })
      ) {
        return total
      }

      let totalPrice

      // Tax inclusive stores
      if (isTaxInclusive) {
        totalPrice = multiply(add(price, tax), quantity)
      } else {
        totalPrice = multiply(price, quantity)
      }

      return add(totalPrice, total)
    },
    0
  )
}

interface DiscountCalculationParams {
  displayDiscount: string | number
  discountIsPercentage: boolean
  // TODO: Rename this everywhere to discountableTotal
  maxSaleDiscount: string | number
  saleIsReturn: boolean
}

export interface DiscountValidationParams {
  displayDiscount: string | number
  maxDisplayDiscount: string | number
  saleIsReturn: boolean
}

/**
 * Determine whether the given display value of the sale discount is valid. The sale discount is valid if it is a valid
 * number between 0 and the given maximum allowed value.
 *
 * @param displayDiscount The display value of the discount to validate
 * @param maxDisplayDiscount The maximum allowed value of the displayDiscount
 * @param saleIsReturn Whether the sale is a return sale or not
 *
 * @returns true if the display discount value is a valid sale discount, false otherwise
 */
export function isSaleDiscountValid({
  displayDiscount,
  maxDisplayDiscount,
  saleIsReturn,
}: DiscountValidationParams) {
  // The sale discount on a return sale cannot be modified and is calculated specially when loading the sale - it
  // typically won't comply with the standard rules (i.e. will be negative etc) but since it can't be modified
  // we simply declare it as valid regardless.
  if (saleIsReturn) {
    return true
  }

  if (!isFinite(displayDiscount)) {
    return false
  }

  const discount = vn(displayDiscount)

  if (discount.lt(0)) {
    return false
  }

  if (discount.gt(maxDisplayDiscount)) {
    return false
  }

  return true
}

interface MaxDisplayDiscountCalculationParams {
  discountableTotal: string | number
  discountIsPercentage: boolean
  maxPercentageDiscount?: string | number
}

/**
 * Calculate the maximum allowed value for the sale displayDiscount based on the givendiscountableTotal and
 * maxPercentageDiscount. If the sale discount is a percentage this will just be the given maxPercentageDiscount,
 * otherwise it will be calculated by applying the maxPercentageDiscount to the given discountableTotal.
 *
 * @param discountableTotal The total value of the sale that can be discounted (inclusive/exclusive as appropriate)
 * @param discountIsPercentage Whether the sale discount is applied as a percentage or not
 * @param maxPercentageDiscount The maximum percentage discount allowed. Defaults to 100% but some users may be
 *        restricted by user permissions.
 *
 * @returns the maximum allowed value for the sale displayDiscount
 */
export function calculateMaxDisplayDiscount({
  discountableTotal,
  discountIsPercentage,
  maxPercentageDiscount = 100,
}: MaxDisplayDiscountCalculationParams): string | number {
  if (discountIsPercentage) {
    return maxPercentageDiscount
  }

  const maxDiscountDecimal = divide(maxPercentageDiscount, 100)
  return round(multiply(discountableTotal, maxDiscountDecimal), 2)
}

/**
 * Get the discount percentage for a sale as a decimal value (e.g. 0.2 for 20%).
 *
 * @param sale The sale to determine the discount percentage of
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @returns The percentage of the sale the discount represents as a decimal
 */
export function calculateSaleDiscountPercentageAsDecimal(
  sale: Readonly<Sale>,
  isTaxInclusive: boolean
): number {
  const maxSaleDiscount = calculateDiscountableTotal({
    lineItems: sale.lineItems,
    saleIsReturn: isReturn(sale),
    saleIsConfirmed: sale.isConfirmed(),
    saleIsSavedUnfulfilled: sale.isSavedUnfulfilled,
    isTaxInclusive,
  })
  return calculateDiscountPercentageAsDecimal({
    displayDiscount: sale.displayDiscount,
    discountIsPercentage: sale.discountIsPercentage,
    maxSaleDiscount,
    saleIsReturn: isReturn(sale),
  })
}

interface SaleLineItemTotalsCalculationParams
  extends BaseLineItemTotalsCalculationParams {
  lineItems: Array<Readonly<LineItem>>
  maxSaleDiscount: string | number
}

/**
 * Calculates the line item totals and tax components for all the given line items and evenly proportions the sale
 * discount across the line items.
 */
export function calculateSaleLineItemTotals({
  lineItems,
  maxSaleDiscount,
  discountPercentAsDecimal,
  returnDiscountPercentAsDecimal,
  isTaxInclusive,
  ...lineItemTotalsParams
}: SaleLineItemTotalsCalculationParams): LineItemTotalsAndTaxComponents[] {
  // calculate the total discount in order to be very accurate
  const discountPercentageToApply =
    returnDiscountPercentAsDecimal || discountPercentAsDecimal

  let totalDiscount = multiply(
    discountPercentageToApply,
    round(maxSaleDiscount, 2)
  )

  return lineItems.map((lineItem, i) => {
    const { totals, taxComponents } = calculateLineItemTotals({
      lineItem,
      discountPercentAsDecimal,
      returnDiscountPercentAsDecimal,
      isTaxInclusive,
      ...lineItemTotalsParams,
    })

    if (totals.saleDiscount) {
      if (isTaxInclusive) {
        const saleDiscountInclusivePrice = add(
          totals.saleDiscountTax,
          totals.saleDiscount
        )
        totalDiscount = subtract(totalDiscount, saleDiscountInclusivePrice)
      } else {
        totalDiscount = subtract(totalDiscount, totals.saleDiscount)
      }
    }

    // from the difference between the total discount and the sum of the line item discount,
    // we adjust the price to eliminate rounding issues.
    if (i === lineItems.length - 1 && totalDiscount) {
      if (!isTaxInclusive) {
        return {
          totals: {
            ...totals,
            saleDiscount: add(totals.saleDiscount, totalDiscount),
          },
          taxComponents,
        }
      }

      if (!taxComponents.length) {
        // TODO: Unsure what to do here
        return { totals, taxComponents }
      }

      const lastItemRate = taxComponents[0].rate.rate
      const discountComponents = calculateTaxInclusivePriceComponents(
        totalDiscount,
        lastItemRate
      )

      return {
        totals: {
          ...totals,
          saleDiscount: add(totals.saleDiscount, discountComponents.price),
          saleDiscountTax: add(totals.saleDiscountTax, discountComponents.tax),
        },
        taxComponents,
      }
    }

    return { totals, taxComponents }
  })
}

type MinSaleDiscountParams = Array<
  Pick<LineItemTotals, 'saleDiscount' | 'saleDiscountTax'>
>

/**
 * Goes through each line item's sale discount information, calculates and
 * returns the discount price and tax information.
 *
 * @param lineItemSaleDiscounts Array of sale discount information applied to
 * each line item
 * @returns The total discount price and tax information of a sale or null if
 * the sale has no discounts
 */
export function calculateSaleDiscount(
  lineItemSaleDiscounts: MinSaleDiscountParams
): MinSaleDiscount | null {
  const price = calculateSaleDiscountPrice(lineItemSaleDiscounts)
  const tax = calculateSaleDiscountTax(lineItemSaleDiscounts)

  if (vn(add(price, tax)).abs().gt(0)) {
    return {
      price,
      tax,
    }
  }

  return null
}

type SalePriceAndTaxTotals = Pick<
  Totals,
  | 'exclusivePrice'
  | 'exclusiveBasePrice'
  | 'tax'
  | 'baseTax'
  | 'inclusivePrice'
  | 'inclusiveBasePrice'
>

/**
 * Calculates and returns the fields related to sale total price and tax
 *
 * @param lineItemTotals An array of line item total objects
 * @returns The total price and tax information fields of a sale
 */
export function calculateSalePriceAndTaxTotals(
  lineItemTotals: LineItemTotals[]
): SalePriceAndTaxTotals {
  const totals: SalePriceAndTaxTotals = {
    exclusivePrice: 0,
    exclusiveBasePrice: 0,
    tax: 0,
    baseTax: 0,
    inclusivePrice: 0,
    inclusiveBasePrice: 0,
  }
  // Calculate totals
  return lineItemTotals.reduce(
    (t, lineItemTotal) => ({
      exclusivePrice: round(
        add(t.exclusivePrice, lineItemTotal.exclusivePrice),
        SALE_PRICE_TAX_DECIMAL_PLACES
      ),
      exclusiveBasePrice: round(
        add(t.exclusiveBasePrice, lineItemTotal.exclusiveBasePrice),
        SALE_PRICE_TAX_DECIMAL_PLACES
      ),
      tax: round(add(t.tax, lineItemTotal.tax), SALE_PRICE_TAX_DECIMAL_PLACES),
      baseTax: round(
        add(t.baseTax, lineItemTotal.baseTax || 0),
        SALE_PRICE_TAX_DECIMAL_PLACES
      ),
      inclusivePrice: round(
        add(t.inclusivePrice, lineItemTotal.inclusivePrice),
        SALE_PRICE_TAX_DECIMAL_PLACES
      ),
      inclusiveBasePrice: round(
        add(t.inclusiveBasePrice, lineItemTotal.inclusiveBasePrice),
        SALE_PRICE_TAX_DECIMAL_PLACES
      ),
    }),
    totals
  )
}

type MinPayment = Pick<Payment, 'amount'> & {
  paymentType: Pick<ApiRetailerPaymentType, 'type_id'>
}
type CalculateSaleCashDiscountingAdjustmentsArgs = {
  saleInclusivePrice: string | number
  discount: MinSaleDiscount | null
  payments: MinPayment[]
  cashDiscountingRate: string
  userRemovedSaleServiceFee: boolean
}
type SaleCashAdjustments = Pick<
  Totals,
  'saleServiceFee' | 'saleServiceFeePaid' | 'cashDiscount'
>
/**
 * Calculates and returns the cash discounting adjustment fields of a sale
 *
 * @param saleInclusivePrice Total inclusive price of a sale
 * @param discount The sale-level discount (or null if there is no discount)
 * @param payments Payments of the sale
 * @param cashDiscountingRate Discount rate when the sale is paid in cash. This
 * will be '0' id the cash discounting is not enabled
 * @param userRemovedSaleServiceFee Flag that keeps track if the cashier has
 * removed the sale service fee
 * @returns Sale cash adjustment fields
 */
export function calculateSaleCashDiscountingAdjustments({
  saleInclusivePrice,
  discount,
  payments,
  cashDiscountingRate,
  userRemovedSaleServiceFee,
}: CalculateSaleCashDiscountingAdjustmentsArgs): SaleCashAdjustments {
  const emptyAdjustments: SaleCashAdjustments = {
    saleServiceFee: '0',
    saleServiceFeePaid: '0',
    cashDiscount: '0',
  }

  if (vn(cashDiscountingRate).eq(0) || userRemovedSaleServiceFee) {
    return emptyAdjustments
  }

  const saleServiceFee = calculateServiceFee(
    saleInclusivePrice,
    discount,
    cashDiscountingRate
  )

  let saleServiceFeePaid = calculateServiceFeePaid(
    payments,
    cashDiscountingRate
  )
  let cashDiscount: string | number = calculateCashDiscount(
    payments,
    cashDiscountingRate
  )

  // Handle over payments of non cash payment types
  if (vn(saleServiceFeePaid).gt(saleServiceFee)) {
    saleServiceFeePaid = saleServiceFee
  }

  // Handle over payments of cash payment types
  if (vn(add(saleServiceFee, cashDiscount)).lt(saleServiceFeePaid)) {
    cashDiscount = subtract(saleServiceFeePaid, saleServiceFee)
  }

  const cashDiscountingInclusivePrice = add(
    saleInclusivePrice,
    saleServiceFee,
    cashDiscount
  )

  // A negative inclusive price at this point is representative of a return. A cash discounting service fee
  // can only be applied to sales with negative inclusive price.
  if (vn(cashDiscountingInclusivePrice).isNeg()) {
    return emptyAdjustments
  } else {
    return {
      saleServiceFee,
      saleServiceFeePaid,
      cashDiscount,
    }
  }
}

/**
 * Calculates the applicable service fee based on the given inclusive price, sale discount and cash discounting rate.
 *
 * @param saleInclusivePrice Total inclusive price of a sale
 * @param discount The sale-level discount (or null if there is no discount)
 * @param cashDiscountingRate Discount rate when the sale is paid in cash. This will be '0' id the cash discounting
 *        is not enabled
 *
 * @returns The applicable service fee
 */
export function calculateServiceFee(
  saleInclusivePrice: string | number,
  discount: MinSaleDiscount | null,
  cashDiscountingRate: string
) {
  let inclusivePrice = saleInclusivePrice
  if (discount && isFinite(discount.price)) {
    inclusivePrice = subtract(
      saleInclusivePrice,
      add(discount.price, discount.tax)
    )
  }

  return round(multiply(inclusivePrice, cashDiscountingRate))
}

/**
 * Calculates the proportion of the service fee already paid.
 *
 * @param  payments Slim version of the payments on sale.
 * @returns Calculated service fee paid
 */
export function calculateServiceFeePaid(
  payments: MinPayment[],
  cashDiscountingRate: string
) {
  if (vn(cashDiscountingRate).eq(0)) {
    return 0
  }

  // This is necessary as eligible payments are assumed to include the service fee already. So we divide by 1+rate to
  // find the pre-fee amount to then calculate the service fee from.
  const cardProcessingRate = divide(
    cashDiscountingRate,
    add(1, cashDiscountingRate)
  )

  const eligibleNonCashPayments = payments.reduce((adjustedPrice, payment) => {
    if (!isEligibleForCashDiscount(payment.paymentType.type_id)) {
      return add(adjustedPrice, payment.amount)
    } else {
      return adjustedPrice
    }
  }, 0)

  return round(multiply(eligibleNonCashPayments, cardProcessingRate))
}

/**
 * Calculates the cash discount for the sale.
 *
 * @param  payments Slim version of payments on the sale.
 * @returns The total cash discount amount (represented as a negative
 * amount)
 */
export function calculateCashDiscount(
  payments: MinPayment[],
  cashDiscountingRate: string
) {
  if (vn(cashDiscountingRate).eq(0)) {
    return 0
  }

  const totalEligibleCashPayments = payments.reduce(
    (adjustedPrice, payment) => {
      if (isEligibleForCashDiscount(payment.paymentType.type_id)) {
        return add(adjustedPrice, payment.amount)
      } else {
        return adjustedPrice
      }
    },
    0
  )

  const cashDiscount = multiply(totalEligibleCashPayments, cashDiscountingRate)
  const cashDiscountAsNegAmount = multiply(cashDiscount, -1)

  return round(cashDiscountAsNegAmount)
}

export type CalculateSaleFinalsArguments = {
  saleTotals: Totals
  lineItemTaxComponents: LineItemTaxComponent[][]
  noTaxGroup: ApiTax | null
  discount: MinSaleDiscount | null
  saleIsConfirmed: boolean
  saleIsSavedUnfulfilled: boolean
  saleIsSavedNonTax: boolean
  isTaxInclusive: boolean
  adjustments: SaleAdjustment[]
  surcharge: SurchargeArgs
  ecomCustomCharges: EcomCustomCharges | null
}
/**
 * Calculates the final values of a sale
 */
export function calculateSaleFinals({
  saleTotals,
  lineItemTaxComponents,
  discount,
  noTaxGroup,
  saleIsConfirmed,
  saleIsSavedUnfulfilled,
  saleIsSavedNonTax,
  isTaxInclusive,
  adjustments,
  surcharge,
  ecomCustomCharges,
}: CalculateSaleFinalsArguments): Finals {
  let { exclusivePrice, tax, saleServiceFee, cashDiscount } = saleTotals
  // LSPay cash discount amount from priceManager. On forward sales it is '0'
  // until an eligible cash payment exists; on returns it is set when the rate
  // was carried from the original sale. Folding it into inclusivePrice adjusts
  // total / outstanding balance accordingly (negative on sale, positive on return).
  const lspCashDiscount = saleTotals.lspCashDiscount ?? '0'

  const tipTotal = totalTipFromAdjustments(adjustments)

  if (discount) {
    exclusivePrice = subtract(exclusivePrice, discount.price)
    tax = subtract(tax, discount.tax)
  }

  // Use the same 5dp precision that baseSaleFinals exposes (and that
  // applyLspCashDiscountToOutstanding receives via saleFinals.exclusivePrice /
  // saleFinals.tax). Without this alignment the raw intermediate precision can
  // push the 2dp-rounded inclusivePrice across a cent boundary relative to the
  // preview, leaving a residual outstanding balance on the exact-pay flow.
  const taxableSubtotalBeforeLspDiscount = vn(round(exclusivePrice, 5))
  const taxBeforeLspDiscount = vn(round(tax, 5))
  const taxableSubtotalAfterLspDiscount = taxableSubtotalBeforeLspDiscount.plus(
    lspCashDiscount
  )
  const adjustedTax =
    !taxableSubtotalBeforeLspDiscount.eq(0) &&
    !taxBeforeLspDiscount.eq(0) &&
    !vn(lspCashDiscount).eq(0)
      ? taxBeforeLspDiscount
          .times(taxableSubtotalAfterLspDiscount)
          .div(taxableSubtotalBeforeLspDiscount)
      : taxBeforeLspDiscount
  tax = adjustedTax.toString()

  let inclusivePrice = add(
    exclusivePrice,
    saleServiceFee,
    cashDiscount,
    lspCashDiscount,
    tax,
    tipTotal
  )

  if (ecomCustomCharges) {
    inclusivePrice = add(inclusivePrice, ecomCustomCharges.totalIncl)
  }

  // If it is a sale requires payment, we should get the surcharge total from the payments
  // Otherwise, we should show the refundable surcharge from the original sale's surcharges
  const surchargeTotal = vn(inclusivePrice).gte(0)
    ? surcharge.amount
    : totalSurchargeFromOriginalSurcharge(inclusivePrice, surcharge.origin)
  const surchargeRules = vn(inclusivePrice).gte(0) ? surcharge.rules : ['']

  const surchargeDisplayProperties = {
    surcharge: round(surchargeTotal, 2),
    surchargeRules,
  }

  // add surcharge to the inclusive price
  inclusivePrice = add(inclusivePrice, surchargeTotal)

  /**
   * Subtotal for a sale
   * For an inclusive retailer we return the exclusive price where the discount
   * has already been deducted, this is because when rendering the discount is
   * displayed before the subtotal
   *
   * For an exclusive retailer we return the exclusive price without discount
   * deducted, this is because when rendering the discount is displayed after
   * the subtotal
   */
  const subtotal = isTaxInclusive ? exclusivePrice : saleTotals.exclusivePrice

  const taxes = calculateSaleFinalTaxes({
    lineItemTaxComponents,
    saleIsConfirmed,
    saleIsSavedUnfulfilled,
    saleIsSavedNonTax,
    taxMultiplier:
      !taxBeforeLspDiscount.eq(0) && !adjustedTax.eq(taxBeforeLspDiscount)
        ? adjustedTax.div(taxBeforeLspDiscount).toString()
        : '1',
  })

  const taxDisplayProperties = calculateSaleFinalTaxDisplayProperties({
    finalTaxes: taxes,
    lineItemTaxComponents,
    noTaxGroup,
    saleIsConfirmed,
  })

  return {
    exclusivePrice: round(exclusivePrice, 5),
    inclusivePrice: round(inclusivePrice, 2),
    tax: round(tax, 5),
    tip: round(tipTotal, 2),
    subtotal: round(subtotal, 5),
    taxes,
    taxDisplayProperties,
    surchargeDisplayProperties,
    ecomCustomCharges,
  }
}

interface calculateSaleDisplayStatusArguments {
  saleStatus: string | undefined
  saleIsReturn: boolean
  lineItemsCount: number
  returnLineItemsCount: number
}
/**
 * Determines the display status of a sale based on the returns information
 * available on a sale
 */
export function calculateSaleDisplayStatus({
  saleStatus,
  saleIsReturn,
  lineItemsCount,
  returnLineItemsCount,
}: calculateSaleDisplayStatusArguments): SaleStatus | undefined {
  // Return the existing sale status if there is no return information.
  if (!saleIsReturn) {
    return saleStatus ? (saleStatus as SaleStatus) : undefined
  }

  // A sale is deemed an exchange if the count of line items is greater than
  // the items returned in the same sale
  const isExchange = lineItemsCount > returnLineItemsCount

  // Only parked and completed sale statuses should be considered exchanges or returns
  if (saleStatus === SaleStatus.PARKED) {
    if (isExchange) {
      return SaleStatus.EXCHANGE_PARKED
    }
    return SaleStatus.RETURN_PARKED
  } else if (saleStatus === SaleStatus.CLOSED) {
    if (isExchange) {
      return SaleStatus.EXCHANGE_CLOSED
    }
    return SaleStatus.RETURN_CLOSED
  }

  // We shouldn't get here, but don't consider a confirmed layby or confirmed account sale a return or exchange.
  return saleStatus ? (saleStatus as SaleStatus) : undefined
}

function calculateSaleDiscountPrice(
  lineItemSaleDiscounts: MinSaleDiscountParams
): number {
  return lineItemSaleDiscounts.reduce((s, lineItemTotal) => {
    return add(s, lineItemTotal.saleDiscount)
  }, 0)
}

function calculateSaleDiscountTax(
  lineItemSaleDiscounts: MinSaleDiscountParams
): number {
  return lineItemSaleDiscounts.reduce((s, lineItemTotal) => {
    return add(s, lineItemTotal.saleDiscountTax)
  }, 0)
}

function calculateDiscountPercentageAsDecimal(
  discountCalcParams: DiscountCalculationParams
): number {
  const {
    displayDiscount,
    discountIsPercentage,
    maxSaleDiscount,
    saleIsReturn,
  } = discountCalcParams

  const maxDisplayDiscount = calculateMaxDisplayDiscount({
    discountableTotal: maxSaleDiscount,
    discountIsPercentage,
  })

  if (
    !isSaleDiscountValid({ displayDiscount, maxDisplayDiscount, saleIsReturn })
  ) {
    return 0
  }

  if (discountIsPercentage) {
    return divide(displayDiscount, 100)
  }

  if (vn(maxSaleDiscount).gt(0)) {
    return divide(displayDiscount, maxSaleDiscount)
  }

  return 0
}

interface SaleFinalTaxesArguments {
  lineItemTaxComponents: LineItemTaxComponent[][]
  saleIsConfirmed: boolean
  saleIsSavedUnfulfilled: boolean
  saleIsSavedNonTax: boolean
  taxMultiplier?: number | string
}
/**
 * Calculate the final total of each tax component in a sale
 * Also, sets a flag to remove the tax from a sale
 */
function calculateSaleFinalTaxes({
  lineItemTaxComponents,
  saleIsConfirmed,
  saleIsSavedUnfulfilled,
  saleIsSavedNonTax,
  taxMultiplier = 1,
}: SaleFinalTaxesArguments) {
  return lineItemTaxComponents.reduce((taxes, taxComponents) => {
    // - 0.x sale API requires a single tax_id per line item (hence the existence of tax groups)
    // - if we remove a single tax from a 3+ tax group, we can't guaranteed that another tax group exists containing
    //   exactly the other 2 rates
    // - for this reason we prevent removing individual tax rates from tax groups with 3 or more rates
    //   (but allow you to remove the entire group cos then we just apply the “no tax” tax rate)
    const shouldMarkUnremovable = taxComponents.length >= 3

    taxComponents.forEach(taxComponent => {
      if (!taxes[taxComponent.rate_id]) {
        taxes[taxComponent.rate_id] = {
          total: 0,
          name: taxComponent.rate.name,
          removable:
            !shouldMarkUnremovable &&
            ((!saleIsConfirmed && !saleIsSavedUnfulfilled) ||
              saleIsSavedNonTax),
          rate: taxComponent.rate,
        }
      }

      taxes[taxComponent.rate_id].total = add(
        taxes[taxComponent.rate_id].total,
        vn(taxMultiplier).eq(1)
          ? taxComponent.total_tax_after_sale_discount
          : round(
              multiply(
                taxComponent.total_tax_after_sale_discount,
                taxMultiplier
              ),
              5
            )
      )
    })

    return taxes
  }, {} as Record<string, SaleTax>)
}

interface SaleFinalTaxDisplayPropertiesArguments {
  finalTaxes: Record<string, SaleTax>
  lineItemTaxComponents: LineItemTaxComponent[][]
  noTaxGroup: ApiTax | null
  saleIsConfirmed: boolean
}
/**
 * Calculate the display properties of sale taxes
 * TODO: These properties should be close to the UI layer and not in the
 * middleware. Helps to translate the strings and symbols as well.
 *
 */
function calculateSaleFinalTaxDisplayProperties({
  finalTaxes,
  lineItemTaxComponents,
  noTaxGroup,
  saleIsConfirmed,
}: SaleFinalTaxDisplayPropertiesArguments) {
  const hasIndividuallyUnremovableTax = lineItemTaxComponents.some(
    lt => lt.length >= 3
  )
  const displayProperties = {
    taxName: '',
    showRemoveAll: hasIndividuallyUnremovableTax && !saleIsConfirmed,
    showTaxBreakdown: true,
  }
  let numTaxes = Object.keys(finalTaxes).length
  const hasNoTaxGroup = noTaxGroup
    ? Object.keys(finalTaxes).includes(noTaxGroup.rates[0].id)
    : false

  if (noTaxGroup && hasNoTaxGroup && numTaxes > 1) {
    delete finalTaxes[noTaxGroup.rates[0].id]
    numTaxes = Object.keys(finalTaxes).length
  } else if (hasNoTaxGroup || numTaxes === 0) {
    // We've only got the no tax group? Don't show the breakdown
    displayProperties.showTaxBreakdown = false
  }

  if (numTaxes > 1) {
    // TODO: This string should be translated
    displayProperties.taxName = `${numTaxes} Taxes`
  } else {
    if (numTaxes === 1) {
      // Just grab the only tax's name.
      const taxComponent = Object.values(finalTaxes)[0]
      displayProperties.taxName = `${taxComponent.name}`

      if (taxComponent.name !== 'No Tax') {
        // TODO: This format needs to be translated
        displayProperties.taxName += ` ${multiply(
          taxComponent.rate.rate,
          100
        )}%`
      }
    }
  }

  return displayProperties
}

/**
 * Get total tip amount from sale.adjustments
 */
export const totalTipFromAdjustments = (adjustments: SaleAdjustment[]) => {
  return adjustments.reduce((accumulator, adjustment) => {
    if (adjustment.type === 'TIP') {
      accumulator = accumulator + adjustment.total
    }
    return accumulator
  }, 0)
}

/**
 * Get total surcharge amount from payments
 */
export const totalSurchargeFromPayments = (payments: Payment[]): number => {
  const filteredSurchargeAmounts = payments
    .filter(payment => payment.surchargeData)
    .map(payment => vn(payment.surchargeData?.amount))

  if (filteredSurchargeAmounts.length > 0) {
    return add(...filteredSurchargeAmounts)
  }
  return 0
}

/**
 * Get surcharge rules from payments
 */
export const getSurchargeRules = (payments: Payment[]) => {
  return payments
    .filter(payment => payment.surchargeData)
    .map(payment => payment.surchargeData?.rule)
    .filter((rule: string | undefined): rule is string => rule !== undefined)
}

/**
 * Calculate the refundable surcharge from the original sale's surcharges (rounded to 2 decimal places).
 *  For example, if a sale returns items with value of 60 USD from a total sale of 100 USD,
 *  where a surcharge of 1.50 USD was applied.
 *  The current return sale should refund a prorated surcharge 0.90 USD
 *  (the 1.50 USD surcharge, multiplied by 60/100).
 */
export const totalSurchargeFromOriginalSurcharge = (
  currentPrice: number,
  originalSurcharge: OriginalSurcharge | null
) => {
  // If there is no original surcharge or net payments was 0, refundable surcharge is 0
  // Otherwise, surcharge is prorated based on the current price and original net payment amount
  // ensure the refundable surcharge is no more than the original surcharge
  const surcharge =
    originalSurcharge === null || originalSurcharge.netPaymentAmount === 0
      ? 0
      : Math.max(
          multiply(
            originalSurcharge.amount,
            divide(currentPrice, originalSurcharge.netPaymentAmount)
          ),
          0 - originalSurcharge.amount
        )
  // round to 2 decimal places because surcharge is a monetary value
  return round(surcharge, 2)
}

/**
 * Calculate the prorated surcharge for a refund payment.
 * This is used to distribute the surcharge into multiple partial refunds.
 * For example, a return sale should refund $102 where a refundable surcharge of $2 was applied.
 * The cashier makes a cash refund of $50, the surcharge for this payment should be 50/102 * $2 = 0.98.
 *    And then refunds the remaining $52 by LSPay, which contains the remaining surcharge $2 - $0.98 = $1.02.
 */
export const calculatePaymentSurchargeForRefund = (
  refund: number | string,
  saleSurcharge: number | string,
  saleInclusive: number | string,
  saleOutstandingBalance: number,
  refundedSurcharge: number
): number | undefined => {
  // If the sale doesn't have refundable surcharge or payment is not a refund, no need to calculate the surcharge data for the payment
  if (vn(saleSurcharge).gte(0) || vn(refund).gte(0)) {
    return undefined
  }

  let surcharge: number
  // If the refund could make the sale fully refunded (balance equal or greater than zero),
  // then the surcharge should be the rest of refundable surcharge
  // It ensures the sum of surcharges in the refund is equal to the total refundable surcharge in the sale
  if (subtract(saleOutstandingBalance, refund) >= 0) {
    surcharge = subtract(saleSurcharge, refundedSurcharge)
  } else {
    // Otherwise, the surcharge should be the proportion of the refundable surcharge in the sale
    surcharge = vn(
      round(multiply(divide(refund, saleInclusive), saleSurcharge), 2)
    ).toNumber()
  }

  return surcharge
}
